import * as React from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, Coins, Timer, Trophy, X } from 'lucide-react'
import { sb } from '@/lib/supabase'
import { useAuth } from '@/auth/auth-provider'
import { displayName } from '@/lib/format'
import { cn } from '@/lib/utils'
import type { GameBet, GamePlayer, GameRound, GameSession, TeamMember } from '@/lib/types'
import {
  MAX_ON_TRACK,
  MIN_BOX,
  ROUND_SECONDS,
  scoreboard,
  secondsLeft,
  sideOf,
  TRACK,
  wagerFrom,
  WELCOME_SECONDS,
} from '@/lib/sheep-race'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ErrorNote, Loading } from '@/components/page'

/**
 * Sheep Race (migrations/0046_games.sql).
 *
 * Everything that decides anything happens in the database: the dice, the coin
 * bonus, the round winner, the match. This page places coins, presses Roll, and
 * draws what came back. That split is not architectural tidiness — it is the
 * only reason the game is worth playing, because a client that could roll its
 * own dice could win every round.
 *
 * Realtime carries the result to the rest of the room. Everyone watches the same
 * roll land at the same moment, which is the whole point of playing it together;
 * the poll underneath is the fallback for a realtime connection that is down.
 */
export function SheepRacePage() {
  const { sessionId } = useParams<{ sessionId: string }>()
  const { session: auth, currentTeam } = useAuth()
  const userId = auth?.user?.id

  const [race, setRace] = React.useState<GameSession | null>(null)
  const [players, setPlayers] = React.useState<GamePlayer[]>([])
  const [rounds, setRounds] = React.useState<GameRound[]>([])
  const [bets, setBets] = React.useState<GameBet[]>([])
  const [roster, setRoster] = React.useState<TeamMember[]>([])
  const [error, setError] = React.useState<string | null>(null)
  const [busy, setBusy] = React.useState(false)
  const [loading, setLoading] = React.useState(true)

  const load = React.useCallback(async () => {
    if (!sessionId) return
    const [s, p, r, b] = await Promise.all([
      sb().from('game_sessions').select('*').eq('id', sessionId).maybeSingle(),
      sb().from('game_players').select('*').eq('session_id', sessionId),
      sb().from('game_rounds').select('*').eq('session_id', sessionId).order('round_no'),
      sb().from('game_bets').select('*').eq('session_id', sessionId),
    ])
    if (s.error) setError(s.error.message)
    setRace((s.data ?? null) as GameSession | null)
    setPlayers((p.data ?? []) as GamePlayer[])
    setRounds((r.data ?? []) as GameRound[])
    setBets((b.data ?? []) as GameBet[])
    setLoading(false)
  }, [sessionId])

  React.useEffect(() => {
    void load()
  }, [load])

  // Names for the coins. Read once from the roster rather than joined onto
  // every bet: it is a dozen rows that do not change during a race.
  React.useEffect(() => {
    if (!currentTeam) return
    void (async () => {
      const { data } = await sb().from('team_members').select('*').eq('team_id', currentTeam.id)
      setRoster((data ?? []) as TeamMember[])
    })()
  }, [currentTeam])

  // ---- realtime -------------------------------------------------------------
  React.useEffect(() => {
    if (!sessionId) return
    const ch = sb()
      .channel(`sheep-${sessionId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'game_sessions', filter: `id=eq.${sessionId}` },
        () => void load(),
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'game_rounds', filter: `session_id=eq.${sessionId}` },
        () => void load(),
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'game_bets', filter: `session_id=eq.${sessionId}` },
        () => void load(),
      )
      .subscribe()
    // A race lasts minutes and a dropped subscription would strand the room on
    // a stale board, so a slow poll runs underneath regardless.
    const t = window.setInterval(() => void load(), 4000)
    return () => {
      window.clearInterval(t)
      void sb().removeChannel(ch)
    }
  }, [sessionId, load])

  // A ticking clock, one second at a time. The DEADLINE is a column (0049), so
  // this only decides how often the number on screen changes — eight people in
  // a room have eight clocks, and the one that refuses a late coin is the
  // database's.
  const [now, setNow] = React.useState(() => Date.now())
  React.useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 250)
    return () => window.clearInterval(t)
  }, [])

  // The result window (0050) comes FIRST, because everything below reads it:
  // while it is open the board shows the round that just finished — its sheep
  // where the dice left them, its coins — rather than the empty one being bet
  // on. Declared after its readers, this was a temporal dead zone error on the
  // first render, which `const` gives you and `var` would have hidden.
  const showingResult = secondsLeft(race?.result_until, now) > 0
  // The last round that was actually decided — which is the previous round
  // normally, and THIS round's previous attempt when one was void (0055). Taken
  // as the latest row carrying a winner rather than by arithmetic on round_no.
  const justFinished = [...rounds]
    .filter((r) => r.winner)
    .sort((x, y) => x.round_no - y.round_no || x.attempt - y.attempt)
    .at(-1)
  /** The round the board is drawing: the finished one, or the one being bet on. */
  const shownRound =
    showingResult && justFinished
      ? { round_no: justFinished.round_no, attempt: justFinished.attempt }
      : { round_no: race?.round_no, attempt: race?.attempt_no }

  /**
   * How far the dice carried each sheep on the round being shown, or null while
   * bets are open — there is nothing to mark as collected until they land, and
   * marking the live board would also leak the roll before it happened.
   */
  const shownReach =
    showingResult && justFinished
      ? (() => {
          const roll = justFinished.rolls?.[justFinished.rolls.length - 1]
          if (!roll) return null
          // reach_* is written by 0059; a round rolled before it is drawn plain
          // rather than wrongly.
          return roll.reach_a != null && roll.reach_b != null
            ? { a: roll.reach_a, b: roll.reach_b }
            : null
        })()
      : null

  const mySide = sideOf(players, userId)
  const current =
    rounds.find((r) => r.round_no === race?.round_no && r.attempt === race?.attempt_no) ?? null
  const roundBets = bets.filter(
    (b) => b.round_no === shownRound.round_no && b.attempt === shownRound.attempt,
  )
  /**
   * My side's four coins for the round being bet on — the stack, not my own.
   *
   * The other side's coins are not in `bets` at all while betting is open: the
   * policy in 0055 withholds them until the round is decided, so there is
   * nothing here to filter out and nothing in the browser to go looking for.
   */
  const ourCoins = bets.filter(
    (b) =>
      b.round_no === race?.round_no &&
      b.attempt === race?.attempt_no &&
      sideOf(players, b.user_id) === mySide,
  )
  /** How many more may go on the track: seven is the cap, not eight (0058). */
  const coinsLeft = MAX_ON_TRACK - ourCoins.length
  /** Everything not on the track is still in hand, and scores. */
  const ourWager = wagerFrom(ourCoins.length)
  const left = secondsLeft(race?.bets_close_at, now)
  const taking = race?.status === 'betting' && race.roll_no === 0 && left > 0
  // The hop plays four times and stops, so leaving the class on a finished
  // match costs nothing and saves a timer.
  const celebrating = showingResult || race?.status === 'finished'
  // Whose sheep is bouncing: the side that took the round just shown, or the
  // match itself once it is over. A draw celebrates nobody.
  const winnerOfLastRound =
    race?.status === 'finished'
      ? race.winner === 'draw'
        ? null
        : race.winner
      : justFinished?.winner === 'draw'
        ? null
        : (justFinished?.winner ?? null)
  const board = scoreboard(players, bets, rounds, roster)

  /** Put one of the side's four coins down. A null box is the wager. */
  async function placeCoin(box: number | null) {
    if (!sessionId || busy) return
    setBusy(true)
    const { error: err } = await sb().rpc('place_sheep_coin', {
      p_session_id: sessionId,
      p_box: box,
    })
    setBusy(false)
    if (err) setError(err.message)
    else void load()
  }

  /** Take one back. Anyone on the side may move any of the side's coins. */
  async function removeCoin(coinId: string) {
    if (!sessionId || busy) return
    setBusy(true)
    const { error: err } = await sb().rpc('remove_sheep_coin', { p_coin_id: coinId })
    setBusy(false)
    if (err) setError(err.message)
    else void load()
  }

  // When the clock runs out the round rolls itself. Every client in the room
  // tries, and `p_expected_round` means the first one does it and the rest read
  // the round back — so nobody has to be the designated presser.
  const rolling = React.useRef(false)
  React.useEffect(() => {
    if (!race || race.status === 'finished' || left > 0 || rolling.current) return
    if (!race.bets_close_at) return
    rolling.current = true
    void (async () => {
      await sb().rpc('roll_sheep_race', {
        p_session_id: race.id,
        p_expected_round: race.round_no,
        p_expected_attempt: race.attempt_no,
      })
      await load()
      rolling.current = false
    })()
  }, [race, left, load])

  if (loading) return <Loading label="Finding the sheep…" />
  if (!race) {
    return (
      <div className="mx-auto max-w-lg p-6">
        <ErrorNote error={error ?? 'That race has finished or was cleared away.'} />
        <Button variant="outline" size="sm" className="mt-4" asChild>
          <Link to="/games">Back to Games</Link>
        </Button>
      </div>
    )
  }

  // What the track is drawing: the round that just ended while the result is
  // up, otherwise the one being bet on.
  const round = showingResult && justFinished ? justFinished : current
  const lastRoll = round?.rolls?.[round.rolls.length - 1] ?? null
  const finished = race.status === 'finished'

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-6 sm:px-6">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="icon-sm" asChild aria-label="Back to Games">
          <Link to="/games">
            <ArrowLeft />
          </Link>
        </Button>
        <div className="min-w-0">
          <h1 className="text-xl font-semibold leading-tight">Sheep Race</h1>
          <p className="text-xs text-muted-foreground">
            {finished
              ? 'Full time.'
              : `Round ${race.round_no} of 5${race.attempt_no > 1 ? ` · replay ${race.attempt_no}` : ''}`}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2 text-sm">
          <Badge variant={race.winner === 'a' ? 'success' : 'secondary'} className="tabular-nums">
            White {race.rounds_a}
          </Badge>
          <span className="text-muted-foreground">—</span>
          <Badge variant={race.winner === 'b' ? 'success' : 'secondary'} className="tabular-nums">
            Grey {race.rounds_b}
          </Badge>
        </div>
      </div>

      {error && <ErrorNote error={error} />}

      {finished && (
        <div className="mb-4 flex items-center gap-3 rounded-lg border border-primary/40 bg-primary/5 px-4 py-3">
          <Trophy className="h-5 w-5 shrink-0 text-primary" />
          <div className="text-sm">
            <p className="font-medium">
              {race.winner === 'draw'
                ? 'A dead heat, after six rounds.'
                : `${race.winner === 'a' ? 'White' : 'Grey'} wins the race.`}
            </p>
            <p className="text-muted-foreground">
              {race.rounds_a}–{race.rounds_b} on rounds.
            </p>
          </div>
        </div>
      )}

      {/* The round that just ended, held on screen for a few seconds (0050).
          Without this the sheep moved and the board reset to an empty track in
          the same instant, and the only interesting part of the round was a
          flicker between two betting screens. */}
      {showingResult && justFinished && (
        <div
          className={cn(
            'mb-4 flex flex-wrap items-center gap-3 rounded-lg border px-4 py-3',
            justFinished.winner === 'draw'
              ? 'border-muted-foreground/30 bg-muted/40'
              : 'border-primary/40 bg-primary/5',
          )}
        >
          <Trophy className="h-5 w-5 shrink-0 text-primary" />
          <div className="text-sm">
            <p className="font-medium">
              {justFinished.winner === 'draw'
                ? `Nobody took round ${justFinished.round_no} — playing it again.`
                : `${justFinished.winner === 'a' ? 'White' : 'Grey'} takes round ${justFinished.round_no}.`}
            </p>
            <p className="text-muted-foreground">
              {justFinished.coins_a} — {justFinished.coins_b} on coins
            </p>
            {/* The sum, not just the total: the whole game is this arithmetic,
                and a side that lost by one wants to see which coin did it. */}
            <div className="mt-1 grid gap-0.5 text-xs text-muted-foreground sm:grid-cols-2">
              {(['a', 'b'] as const).map((side) => {
                const roll = justFinished.rolls?.[justFinished.rolls.length - 1]
                if (!roll) return null
                const kept = side === 'a' ? (roll.kept_a ?? 0) : (roll.kept_b ?? 0)
                const back = side === 'a' ? roll.bonus_a : roll.bonus_b
                const pos = side === 'a' ? justFinished.pos_a : justFinished.pos_b
                return (
                  <span key={side} className="tabular-nums">
                    {side === 'a' ? 'White' : 'Grey'}: kept {kept} + back {back} − {12 - pos} short ={' '}
                    <span className="font-medium text-foreground">
                      {side === 'a' ? justFinished.coins_a : justFinished.coins_b}
                    </span>
                  </span>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* ---- the track ------------------------------------------------------ */}
      <Card className="mb-4 overflow-hidden">
        <CardContent className="space-y-4 overflow-x-auto p-4">
          {(['a', 'b'] as const).map((side) => (
            <Track
              key={side}
              side={side}
              mine={mySide === side}
              position={side === 'a' ? (round?.pos_a ?? 0) : (round?.pos_b ?? 0)}
              coins={roundBets.filter((b) => sideOf(players, b.user_id) === side)}
              roster={roster}
              reach={shownReach?.[side] ?? null}
            />
          ))}
        </CardContent>
      </Card>

      {/* ---- the clock and the dice ----------------------------------------- */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        {!finished && (
          <div
            className={cn(
              'flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium tabular-nums',
              left <= 5 && left > 0 && !showingResult && 'border-warning/50 bg-warning/10 text-warning',
            )}
          >
            <Timer className="h-4 w-4" />
            {showingResult
              ? `Round ${race.round_no} in ${secondsLeft(race.result_until, now)}s`
              : left > 0
                ? `${left}s — the dice roll themselves`
                : 'Rolling…'}
            <span className="h-1.5 w-20 overflow-hidden rounded-full bg-muted">
              <span
                className="block h-full bg-primary transition-[width] duration-200 ease-linear"
                style={{
                width: `${Math.min(100, (left / (race.round_no === 1 && race.attempt_no === 1 ? ROUND_SECONDS + WELCOME_SECONDS : ROUND_SECONDS)) * 100)}%`,
              }}
              />
            </span>
          </div>
        )}
        {lastRoll && (
          <div className="flex items-center gap-2 text-sm">
            <Dice
              label="White"
              dice={[lastRoll.die_a, lastRoll.die_a2]}
              bonus={lastRoll.bonus_a}
            />
            <Dice label="Grey" dice={[lastRoll.die_b, lastRoll.die_b2]} bonus={lastRoll.bonus_b} />
          </div>
        )}
      </div>

      {/* ---- your bet ------------------------------------------------------- */}
      {!finished && (
        <Card className="mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="flex flex-wrap items-center gap-2 text-base">
              <Coins className="h-4 w-4" />
              Your side's coins
              {mySide && (
                <Badge variant="secondary">{mySide === 'a' ? 'White' : 'Grey'}</Badge>
              )}
              <span className="ml-auto text-sm font-normal tabular-nums text-muted-foreground">
                {coinsLeft} of {MAX_ON_TRACK} left to place
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Your side holds eight coins and may put seven on the track; anybody can move any of
              them. Your sheep collects every coin it{' '}
              <span className="font-medium text-foreground">runs over</span> — each one comes back
              to you and carries it a box further. You score what you kept, plus what came back,
              minus a coin for every box short of 12 — and the higher score takes the round.
            </p>

            <div>
              <p className="mb-1.5 text-xs font-medium">
                On a box{' '}
                <span className="font-normal text-muted-foreground">
                  — one extra box per coin there, so two on one box is worth two. Then it checks
                  where that left it, so a line of coins is a bridge.
                </span>
              </p>
              <div className="flex flex-wrap gap-1.5">
                {TRACK.map((box) => {
                  const here = ourCoins.filter((c) => c.box === box)
                  return (
                    <button
                      key={box}
                      type="button"
                      disabled={!taking || busy || (coinsLeft <= 0 && here.length === 0)}
                      onClick={() => void placeCoin(box)}
                      className={cn(
                        'relative h-10 w-10 rounded-md border text-sm font-semibold tabular-nums transition-colors',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        'disabled:opacity-50',
                        here.length > 0
                          ? 'border-transparent bg-primary text-primary-foreground'
                          : 'border-input hover:bg-accent',
                      )}
                    >
                      {box}
                      {here.length > 1 && (
                        <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-amber-400 text-[10px] font-bold text-amber-950">
                          {here.length}
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Not a button: the wager is whatever is left (0053), so this is a
                readout that moves as coins go down. Showing it as something you
                place would hide the one decision in the game. */}
            {/* Where the round stands if the dice are kind, and if they are
                not. Two numbers rather than one, because the decision being
                made is a spread and showing its middle would hide that. */}
            <div className="flex flex-wrap items-center gap-4 rounded-md border px-3 py-2">
              <div>
                <span className="text-2xl font-bold tabular-nums">{ourWager}</span>
                <span className="ml-1.5 text-xs text-muted-foreground">in hand</span>
              </div>
              <div className="min-w-0 text-xs text-muted-foreground">
                <p>
                  Finish on 12 and you score{' '}
                  <span className="font-medium text-foreground tabular-nums">{ourWager}</span>
                  {'. '}
                  An average roll lands you near 7, for{' '}
                  <span className="font-medium text-foreground tabular-nums">{ourWager - 5}</span>
                  {' '}before anything comes back.
                </p>
                <p>
                  Every coin the sheep runs over is worth two: it moves you, and it returns. A coin
                  past the dice is spent.
                </p>
              </div>
            </div>

            {/* Placed coins, with a way to pick one back up. Four shared coins
                need an undo that does not require finding whoever placed it. */}
            {ourCoins.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 border-t pt-3">
                <span className="text-xs text-muted-foreground">Placed:</span>
                {ourCoins.map((coin) => (
                  <button
                    key={coin.id}
                    type="button"
                    disabled={!taking || busy}
                    onClick={() => void removeCoin(coin.id)}
                    title="Take this coin back"
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium',
                      'hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      'disabled:opacity-50',
                    )}
                  >
                    box {coin.box}
                    <X className="h-3 w-3 opacity-60" />
                  </button>
                ))}
              </div>
            )}

            {ourCoins.length === 0 && taking && (
              <p className="text-xs text-muted-foreground">
                Nothing placed yet — all eight are on your sheep, which is a perfectly good bet on a
                slow sheep.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* ---- who is on what ------------------------------------------------- */}
      <div className="grid gap-4 sm:grid-cols-2">
        {(['a', 'b'] as const).map((side) => (
          <Card key={side}>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm">
                {/* The team's own sheep. It bounces for a couple of seconds
                    when its side takes a round, which is the cheapest possible
                    way to tell a room who won from across it. */}
                <span
                  className={cn(
                    'inline-block shrink-0',
                    celebrating && winnerOfLastRound === side && 'sheep-celebrating',
                  )}
                >
                  <span
                    className={cn(
                      'sheep sheep-sm sheep-right block',
                      celebrating && winnerOfLastRound === side && 'sheep-running',
                    )}
                    style={{
                      filter: side === 'b' ? 'grayscale(1) brightness(0.72) contrast(1.1)' : undefined,
                    }}
                  />
                </span>
                {side === 'a' ? 'White' : 'Grey'} ·{' '}
                <span className="font-normal text-muted-foreground">
                  {players.filter((p) => p.side === side).length} players
                </span>
                {/* The match, not the round. A side that took the race keeps
                    this for as long as the board is open. */}
                {finished && race.winner === side && (
                  <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                    <Trophy className="h-3.5 w-3.5" />
                    Winners
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-1 text-sm">
                {board
                  .filter((row) => row.side === side)
                  .map((row) => (
                    <li key={row.userId} className="flex items-center justify-between gap-2">
                      <span className="min-w-0 truncate">
                        {row.name}
                        {row.userId === userId && (
                          <span className="ml-1 text-xs text-muted-foreground">(you)</span>
                        )}
                      </span>
                      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                        {row.points} pt{row.points === 1 ? '' : 's'}
                      </span>
                    </li>
                  ))}
                {board.filter((row) => row.side === side).length === 0 && (
                  <li className="text-xs text-muted-foreground">Nobody yet.</li>
                )}
              </ul>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ---- rounds played --------------------------------------------------- */}
      {rounds.length > 0 && (
        <Card className="mt-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Rounds</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm tabular-nums">
              <thead className="text-xs text-muted-foreground">
                <tr>
                  <th className="pb-1 text-left font-normal">Round</th>
                  <th className="pb-1 text-right font-normal">Boxes</th>
                  <th className="pb-1 text-right font-normal">Coins</th>
                  <th className="pb-1 pl-3 text-left font-normal">Took it</th>
                </tr>
              </thead>
              <tbody>
                {rounds.map((r) => (
                  <tr key={r.id} className="border-t">
                    <td className="py-1 text-muted-foreground">
                      {r.round_no}
                      {r.attempt > 1 && <span className="ml-1 text-xs opacity-70">#{r.attempt}</span>}
                    </td>
                    <td className="py-1 text-right">
                      {r.pos_a} — {r.pos_b}
                    </td>
                    <td className="py-1 text-right font-medium">
                      {r.coins_a} — {r.coins_b}
                    </td>
                    <td className="py-1 pl-3 text-xs text-muted-foreground">
                      {r.winner === 'a'
                        ? 'White'
                        : r.winner === 'b'
                          ? 'Grey'
                          : r.winner === 'draw'
                            ? 'Void — replayed'
                            : 'Running'}
                      {/* Worth showing when the two disagree: the sheep that got
                          furthest did not take the round. That is the whole
                          point of counting coins. */}
                      {r.winner && r.lead && r.winner !== r.lead && r.lead !== 'draw' && (
                        <span className="ml-1 opacity-70">
                          ({r.lead === 'a' ? 'White' : 'Grey'} led)
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

/**
 * The track geometry, in pixels.
 *
 * The sheep is positioned with arithmetic rather than by living inside a box,
 * which is what lets it WALK: one element, one `left`, and a transition. Those
 * three numbers therefore have to agree with the classes on the boxes below —
 * `w-10` is 40 and `gap-1` is 4 — so they are named here rather than inlined,
 * and changing a class without changing a number puts the sheep between boxes.
 */
const BOX_W = 40
const BOX_GAP = 4
const STEP = BOX_W + BOX_GAP
const SHEEP_W = 56
/** The pen, left of box 1, where a sheep on box 0 stands. */
const PEN_W = SHEEP_W + 8
/** How long a sheep takes per box. Slow enough to watch, short of tedious. */
const MS_PER_BOX = 150

/** One sheep, its twelve boxes, and the coins on them. */
function Track({
  side,
  mine,
  position,
  coins,
  roster,
  reach,
}: {
  side: 'a' | 'b'
  mine: boolean
  position: number
  coins: GameBet[]
  roster: TeamMember[]
  /**
   * How far the DICE carried this sheep on the roll being shown, or null while
   * bets are open. Every coin at or below it was collected (0059) — drawn apart
   * from the ones that were not, because "which of these came back" is the
   * question a side asks the moment the dice land, and the sum in the banner
   * only gives them the count.
   */
  reach?: number | null
}) {
  const nameFor = (userId: string) => displayName(roster.find((m) => m.user_id === userId) ?? null)

  // Walking is a function of the sheep MOVING, not of the race being in a
  // rolling state: the legs run for exactly as long as the journey takes, and a
  // sheep standing on a box between rolls stands still.
  const [walking, setWalking] = React.useState(false)
  const was = React.useRef(position)
  const distance = Math.abs(position - was.current)
  React.useEffect(() => {
    if (position === was.current) return
    const boxes = Math.abs(position - was.current)
    was.current = position
    setWalking(true)
    const t = window.setTimeout(() => setWalking(false), boxes * MS_PER_BOX)
    return () => window.clearTimeout(t)
  }, [position])

  // Clicking a sheep makes it hop. It changes nothing and is worth having:
  // during a round of betting there is a minute where the board does nothing,
  // and a sheep that answers a poke is better company than one that does not.
  const [hopping, setHopping] = React.useState(false)
  function hop() {
    if (hopping) return
    setHopping(true)
    window.setTimeout(() => setHopping(false), 500)
  }

  // Box 0 is the pen; the track starts at MIN_BOX, so that is the first column.
  const x =
    position < MIN_BOX ? 0 : PEN_W + (position - MIN_BOX) * STEP + (BOX_W - SHEEP_W) / 2

  return (
    <div className={cn('rounded-lg border p-3', mine && 'border-primary/50 bg-primary/5')}>
      <div className="mb-2 flex items-center gap-2 text-xs font-medium">
        {side === 'a' ? 'White' : 'Grey'}
        {mine && <Badge variant="secondary">your side</Badge>}
        <span className="ml-auto text-muted-foreground tabular-nums">box {position}</span>
      </div>

      <div className="relative min-w-max pt-10">
        {/* The sheep floats above the track rather than sitting in a box, so it
            can move between them. */}
        <button
          type="button"
          onClick={hop}
          aria-label={`${side === 'a' ? 'White' : 'Grey'} sheep on box ${position}`}
          className="absolute bottom-14 cursor-pointer transition-[left] ease-linear focus-visible:outline-none"
          style={{
            left: x,
            transitionDuration: `${Math.max(1, distance) * MS_PER_BOX}ms`,
          }}
        >
          {/* The hop is on this inner wrapper and the mirror is on the sprite:
              two transforms on one element and the later one simply wins. */}
          <span className={cn('block', hopping && 'sheep-hopping')}>
            <span
              className={cn('sheep sheep-sm sheep-right block', (walking || hopping) && 'sheep-running')}
              style={{
                // One white sheep on the sheet. Side B is tinted rather than
                // redrawn: cheaper than a second sprite, and it reads instantly.
                filter: side === 'b' ? 'grayscale(1) brightness(0.72) contrast(1.1)' : undefined,
              }}
            />
          </span>
        </button>

        <div className="flex items-end" style={{ gap: BOX_GAP, paddingLeft: PEN_W }}>
          {TRACK.map((box) => {
            const here = coins.filter((c) => c.box === box)
            const occupied = position === box
            return (
              <div
                key={box}
                title={here.map((c) => nameFor(c.user_id)).join(', ')}
                style={{ width: BOX_W }}
                className={cn(
                  'flex h-14 shrink-0 flex-col items-center justify-end rounded border text-[10px]',
                  occupied ? 'border-primary bg-primary/15' : 'border-dashed',
                )}
              >
                {here.length > 0 && (
                  <span
                    className={cn(
                      'mb-0.5 inline-flex items-center rounded-full px-1.5 py-0.5 font-semibold',
                      reach != null && box <= reach
                        ? 'bg-primary text-primary-foreground ring-2 ring-primary/30'
                        : 'bg-amber-400/90 text-amber-950',
                    )}
                    title={
                      reach != null && box <= reach
                        ? 'collected — came back, and moved the sheep a box'
                        : undefined
                    }
                  >
                    {here.length}
                  </span>
                )}
                <span className="mb-0.5 text-muted-foreground tabular-nums">{box}</span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/**
 * One sheep's pair of dice, their total, and the coins they walked onto.
 *
 * The second die is optional: a round rolled before 0051 has one, and showing
 * "3 = 3" for it would be stranger than showing the one face it actually had.
 */
function Dice({
  label,
  dice,
  bonus,
}: {
  label: string
  dice: Array<number | undefined>
  bonus: number
}) {
  const faces = dice.filter((d): d is number => typeof d === 'number')
  const total = faces.reduce((a, b) => a + b, 0)
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs">
      <span className="text-muted-foreground">{label}</span>
      {faces.map((face, i) => (
        <span
          key={i}
          className="inline-flex h-6 w-6 items-center justify-center rounded border bg-card text-sm font-semibold tabular-nums"
        >
          {face}
        </span>
      ))}
      {faces.length > 1 && (
        <span className="font-semibold tabular-nums">= {total}</span>
      )}
      {bonus > 0 && <span className="font-medium text-primary">+{bonus}</span>}
    </span>
  )
}
